// A producer's own seeds, as My Seeds lists them (core API, GET /seeds?mine=1).
//
// Every version is its own seed row, so a flat list reads as four songs all
// called "First Pass" when it is one song taken four times. Grouped by tree
// instead: one entry per song, the newest take leading (that is what a click
// opens), the older takes behind it. The list is the caller's own seeds only,
// so a remix's root may be someone else's seed that is not in it — the group
// is still keyed by that root, which is what makes the takes one song.

import type { SeedSummary } from "@shared/api-types.js";

export interface MySeedGroup {
  rootId: string;
  /** The newest take — what the card opens and titles itself after. */
  latest: SeedSummary;
  /** Every take, newest first (`latest` included). */
  versions: SeedSummary[];
}

export function groupMySeeds(rows: SeedSummary[]): MySeedGroup[] {
  const byRoot = new Map<string, SeedSummary[]>();
  for (const row of rows) {
    const root = row.rootSeedId || row.id;
    byRoot.set(root, [...(byRoot.get(root) ?? []), row]);
  }
  const groups: MySeedGroup[] = [];
  for (const [rootId, members] of byRoot) {
    const versions = [...members].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    groups.push({ rootId, latest: versions[0]!, versions });
  }
  return groups.sort((a, b) => b.latest.createdAt.localeCompare(a.latest.createdAt));
}

/** What a take plays: the master's MP3 preview when there is one, else the
 * master; null with no master at all. */
export function audioOf(seed: Pick<SeedSummary, "masterUrl" | "masterPreviewUrl">): string | null {
  return seed.masterPreviewUrl || seed.masterUrl || null;
}

/**
 * What a song's play button plays: the newest take that has audio. Usually
 * that is `latest`; a take uploaded without a master (legacy, or a render
 * that never finished) falls through to the one before it rather than leaving
 * the button dead while an older take could answer. Null when no take can.
 */
export function previewOf(group: MySeedGroup): { seed: SeedSummary; url: string } | null {
  for (const seed of group.versions) {
    const url = audioOf(seed);
    if (url) return { seed, url };
  }
  return null;
}
